import React, {createContext, useState, useEffect, ReactNode, useRef} from 'react';
import useToken from "../../../common/src/utils/useToken";
import OverviewService from "../services/OverviewService";
import {Entry, ExchangeRateExample, ViewType} from "../types"
import MetricsService from "../../../common/src/services/MetricsService";
import usePrevious from "../../../common/src/utils/customHooks";
import {BonusPointsOverview, RewardShopServiceData} from "../../../common/src/utils/types";
import {cartEvent} from "../../../common/src/utils/cartEvent";

interface OverviewContextProps {
    entries?: Entry[];
    exchangeRateExample: ExchangeRateExample;
    bonusPointsOverview?: BonusPointsOverview;
    giftAmount?: number;
    giftStatus?: boolean | undefined;
    hasAnyRedemption?: boolean;
    redeemableBalance?: number;
    onSendingMetricEvent: (method: string) => void;
}

const OverviewContext = createContext<OverviewContextProps>({} as OverviewContextProps);

type OverviewProviderProps = {
    children: ReactNode;
    view: string;
    siteId: number;
    siteDomain: string;
    lang: string;
    metricsService: MetricsService;
}

export const OverviewProvider: React.FC<OverviewProviderProps> = ({
  children,
  view,
  siteId,
  siteDomain,
  lang,
  metricsService
}: OverviewProviderProps) => {
    const token = view === ViewType.Private ? useToken().token : null;
    const cartBPs = view === ViewType.Private ? cartEvent().cartBPs : null;
    const SET_TIMEOUT_TIMER = 5000;
    const [checkToken, setCheckToken] = useState<boolean>(false);
    const overviewServiceRef = useRef(new OverviewService(metricsService));
    const previousToken = usePrevious(token);
    const [entries, setEntries] = useState<Entry[] | null>(null);
    const [exchangeRateExample, setExchangeRateExample] = useState<ExchangeRateExample | null>(null);
    const [giftAmount, setGiftAmount] = useState<number | null>(null);
    const [giftStatus, setGiftStatus] = useState<boolean | undefined>(undefined);
    const [hasAnyRedemption, setHasAnyRedemption] = useState<boolean>(false);
    const [bonusPointsOverview, setBonusPointsOverview] = useState<BonusPointsOverview | null>(null);
    const [redeemableBalance, setRedeemableBalance] = useState(null);

    const onSendingMetricEvent = (method: string) => {
        (metricsService as any)[method]();
    };

    const updateEntries = (): void => {
        overviewServiceRef.current.onGetEntries(token)
            .then((data: Entry[]) => {
                setEntries(data);
            })
    }

    const updateBPOverview = (): void => {
        overviewServiceRef.current.onGetBPOverview(token, siteDomain, lang)
            .then((data: RewardShopServiceData) => {
                setBonusPointsOverview(data.bonusPointsOverview);
                setGiftAmount(data.giftAmount);
                setGiftStatus(data.giftStatus);
                setHasAnyRedemption(data.hasAnyRedemption);
            })
    }

    const updateExchangeRateExample = (): void => {
        overviewServiceRef.current.onGetExchangeRateExample(siteId)
            .then((data: ExchangeRateExample) => {
                setExchangeRateExample(data);
            })
    }

    useEffect(() => {
        if(bonusPointsOverview) {
            const balance = Math.max(bonusPointsOverview.balanceActive - cartBPs, 0);
            setRedeemableBalance(balance);
        }
    }, [bonusPointsOverview, cartBPs]);

    useEffect(() => {
        const checkToken = setTimeout(() => {
            setCheckToken(true);
        }, SET_TIMEOUT_TIMER);
        return () => clearTimeout(checkToken);
    }, [])

    useEffect(() => {
        if (checkToken && !token && view === "private") {
            onSendingMetricEvent('renderError');
            metricsService.setLogsBuffer('token not received');
        };
    }, [checkToken])

    useEffect(() => {
        if (previousToken === null && view === ViewType.Private) {
            onSendingMetricEvent('firstEventToken');
        }
        if (view === ViewType.Private && token) {
            if (!entries) updateEntries();
            if (!bonusPointsOverview) updateBPOverview();
        }
        if (!exchangeRateExample) updateExchangeRateExample();
    }, [token]);

    return (
        <OverviewContext.Provider
            value={{
                onSendingMetricEvent,
                entries,
                exchangeRateExample,
                bonusPointsOverview,
                giftAmount,
                giftStatus,
                hasAnyRedemption,
                redeemableBalance}}>
            {children}
        </OverviewContext.Provider>
    )
};

export default OverviewContext;
